import React, { useEffect, useState } from "react";
import ECCAPPBANNER from '../../app/assets/images/projectImage/eccprojectImage/banner-ecc.webp'
import APP1 from '../../app/assets/images/projectImage/eccprojectImage/app-1.webp'
import APP2 from '../../app/assets/images/projectImage/eccprojectImage/app-2.webp'
import APP3 from '../../app/assets/images/projectImage/eccprojectImage/app-3.webp'
import AppsTestimonials from "../molecules/AppsTestimonials/AppsTestimonials";
import CompanyLogoImage from "../molecules/CompanyLogoImage/CompanyLogoImage";
import ContactHelp from "../molecules/ContactHelp/ContactHelp";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Footer from "../organisms/Footer/Footer";
import ECCAppBg from '../../app/assets/images/projectImage/eccprojectImage/ecc-bg.webp'; // Update with actual path

const ECCAppPage = () => {
    const [bgLoaded, setBgLoaded] = useState(false);
    const { scrollY } = useScroll();

    // Create animations based on scroll position
    const bannerOpacity = useTransform(scrollY, [0, 300], [1, 0.9]);
    const bannerScale = useTransform(scrollY, [0, 300], [1, 0.95]);

    useEffect(() => {
        // Create a new image object
        const img = new Image();

        // Set up load event handler
        img.onload = () => {
            setBgLoaded(true);
        };

        // Set image source to start loading
        img.src = ECCAppBg;

        // If the image is already cached, onload might not fire
        if (img.complete) {
            setBgLoaded(true);
        }
    }, []);

    // Create dynamic style for background
    const bgStyle = bgLoaded ? {
        backgroundImage: `url(${ECCAppBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    } : {};

    // Animation variants for content sections
    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    const fadeIn = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.8
            }
        }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-eccColor"
        >
            <div style={bgStyle} className={`transition-opacity duration-500 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`}>
                <div className="pt-32 md:pt-52">
                    <motion.div
                        style={{ opacity: bannerOpacity, scale: bannerScale }}
                    >
                        <img src={ECCAPPBANNER} alt="ECC App Banner" className="w-full h-full object-cover" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.7 }}
                        variants={staggerContainer}
                        className='flex justify-center items-center gap-4 pb-6 md:pb-10 px-4 md:px-0'
                    >
                        <div className='text-white flex flex-col gap-2 relative text-center'>
                            <motion.div variants={fadeInUp} className='capitalize font-bold text-3xl md:text-6xl'>ECC App</motion.div>
                            <motion.div variants={fadeInUp} className='font-medium text-lg md:text-2xl'>Turning Patient Check-Ins Into Clinical Intelligence<br />  </motion.div>
                             <motion.div variants={fadeInUp} className='text-[#e0e0e0] font-medium text-base md:text-lg w-[75%] mx-auto'>We built ECC a real-time health tracking platform that gave Dutch pain specialists the data they needed before and after every surgery to prepare better, operate smarter, and recover faster.</motion.div>
                        </div>
                    </motion.div>

                    <Section>
                        <motion.div className='mb-5 flex flex-col md:flex-row-reverse items-center justify-center px-8 lg:px-24 gap-1 md:gap-8 lg:gap-32'>
                            <motion.img
                                src={APP1}
                                className='w-full md:w-[50%]'
                                variants={fadeIn}
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.3 }
                                }}
                            />
                            <motion.div variants={fadeInUp} className="text-center md:text-left">
                                <div className='text-2xl md:text-3xl font-bold'>Overview</div>
                                <div className='text-[#e0e0e0] pt-2 text-base md:text-lg'><span>Excellent Care Clinics is an NVA-recognized pain clinic in the Netherlands offering insurance-covered care for complex chronic and acute pain conditions. As a teaching clinic treating growing patient volumes, they needed a digital system that could keep pace with their clinical standards and the expectations of modern patients.</span>
                             <br /> <span>We built them a purpose-built Android app that digitizes patient health tracking across the entire surgical journey, giving both patients and clinical staff real-time visibility into health progression.</span>
                          
                                </div>
                            </motion.div>
                        </motion.div>
                    </Section>

                    <Section>
                        <motion.div className='mb-5 flex flex-col md:flex-row items-center justify-center px-8 lg:px-24 gap-1 md:gap-8 lg:gap-32 bg-eccAppColor'>
                            <motion.img
                                src={APP2}
                                className='w-full md:w-[50%]'
                                variants={fadeIn}
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.3 }
                                }}
                            />
                            <motion.div variants={fadeInUp} className="text-center md:text-left">
                                <div className='text-2xl md:text-3xl font-bold'>The Challenge</div>
                                <div className='text-[#e0e0e0] pt-2 text-base md:text-lg'><span>Excellent Care Clinics had no reliable way to monitor patients before and after surgical procedures. Intake was manual, follow-ups were inconsistent, and there was no structured data trail to confirm whether a patient was adequately prepared for surgery or recovering on track afterward.</span>
                                <br /><span>Surgeons were making decisions without longitudinal data. Recovery outcomes were harder to optimize. They came to us needing a system that put patient health data at the center of every clinical decision.</span>
                                </div>
                            </motion.div>
                        </motion.div>
                    </Section>

                    <Section>
                        <motion.div className='mb-5 flex flex-col md:flex-row-reverse items-center justify-center px-8 lg:px-24 gap-1 md:gap-8 lg:gap-32'>
                            <motion.img
                                src={APP3}
                                className='w-full md:w-[50%]'
                                variants={fadeIn}
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.3 }
                                }}
                            />
                           <motion.div variants={fadeInUp} className="text-center md:text-left">
    <div className='text-2xl md:text-3xl font-bold'>
        What We Built & The Outcome
    </div>
    <div className='text-[#e0e0e0] pt-2 text-base md:text-lg'>
        We delivered a bilingual (Dutch + English) Android application that guides patients through structured health check-in questionnaires at every stage of their surgical journey pre-op and post-op and surfaces that data in real time to the clinical team.
    </div>
    <div className="mt-6">
        <h3 className="text-xl font-semibold text-white mb-3">
            Outcomes delivered:
        </h3>

        <ul className="space-y-3 text-[#e0e0e0] text-base md:text-lg list-disc pl-5">
            <li>
                Significantly faster and more complete patient intakes
            </li>

            <li>
               Improved surgical outcomes through better pre-surgery body preparation tracking
            </li>

            <li>
               Measurable increase in patient volume as ECC's streamlined digital experience strengthened clinic reputation and capacity

            </li>
        </ul>
    </div>
</motion.div>
                        </motion.div>
                    </Section>

                 <motion.section
    variants={fadeInUp}
    className="mt-16 px-8 lg:px-24 py-12"
>
    <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
        Key Features
    </h2>

    <div className="space-y-8">
        {[
            {
                title: "Phased Health Questionnaires",
                description:
                    "Structured 25+ question flows tailored separately for pre-surgery preparation and post-surgery recovery tracking.",
            },
            {
                title: "Real-Time Data Sync via Firebase",
                description:
                    "Patient submissions instantly reflect across all connected devices with zero manual refresh.",
            },
            {
                title: "Live Staff Notifications",
                description:
                    "Clinical staff receive immediate alerts when a patient completes a check-in, enabling faster response and follow-up.",
            },
            {
                title: "Dual Dashboard Patient & Clinic",
                description:
                    "Patients track their own health progression over time while doctors and nurses monitor all patients from a centralized live dashboard.",
            },
            {
                title: "Bilingual Support",
                description:
                    "Full Dutch and English language support built into the core of the app.",
            },
            {
                title: "Progress Statistics & Visualizations",
                description:
                    "Time-series data presented clearly so both patients and clinicians can see trends at a glance.",
            },
            {
                title: "Pre & Post Surgery Modules",
                description:
                    "Distinct journeys for preparation and recovery, ensuring the right questions are asked at the right stage.",
            },
        ].map((feature, index) => (
            <div key={index}>
                <div className="flex gap-6 items-start">
                    {/* Number */}
                    <div className="text-3xl md:text-4xl font-bold text-blue-500 min-w-[50px]">
                        {String(index + 1).padStart(2, "0")}
                    </div>

                    {/* Content */}
                    <div>
                        <h3 className="text-xl md:text-2xl font-semibold text-white">
                            {feature.title}
                        </h3>

                        <p className="text-[#d0d0d0] mt-2 text-base md:text-lg leading-relaxed">
                            {feature.description}
                        </p>
                    </div>
                </div>

                {index !== 6 && (
                    <div className="h-[2px] w-full bg-blue-500/50 rounded-full"></div>
                )}
            </div>
        ))}
    </div>
</motion.section>



                    <Section>
                        <AppsTestimonials sliderColor='#5D80D5' bgUnActiveColor="bg-[#2849B4]" color='#fff' bgcolor={'bg-[#fff]'} activeTextColor='white' textColor='black' />
                    </Section>

                    <Section>
                        <div className='p-4 md:p-10 px-4 md:px-36'>
                            <CompanyLogoImage borderColor='border-[#5D80D5]' bgColor='bg-[#2849B4]' />
                        </div>
                    </Section>

                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false, amount: 0.3 }}
                        transition={{ duration: 0.8 }}
                        className="px-4 md:px-0"
                    >
                        <ContactHelp title="Let's connect & achieve your business goals together" buttonTitle='CONTACT US' />
                    </motion.div>

                    <Footer />
                </div>
            </div>
        </motion.div>
    );
};

// Section component to handle scroll-based animations
const Section = ({ children }: { children: React.ReactNode }) => {
    const ref = React.useRef(null);

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={{
                hidden: { opacity: 0, y: 50 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: 0.6,
                        ease: "easeOut"
                    }
                }
            }}
        >
            {children}
        </motion.div>
    );
};

export default ECCAppPage;